import { useState } from "react";
import "./ServiceRequestcard.css";
import "./AdvocateCard.css";
import { useNavigate } from "react-router-dom";
import {GavelIcon, CraneIcon, TruckIcon, ToolsIcon, ClipboardIcon, BoltIcon, AlertSmIcon,PackageIcon, ClockIcon, PhoneIcon, PaperclipIcon, PinIcon, MapPinIcon, UserPlusIcon, ChevronRightIcon} from "../UI/icons.jsx";

// ─── Constants ────────────────────────────────────────────────────────────────

export const TRACKABLE_SERVICES = new Set(["crane", "recovery"]);

const GENERIC_STATUS_META = {
  New:        { cls: "qs-status--new",      label: "New"      },
  Assigned:   { cls: "qs-status--assigned", label: "Assigned" },
  "En route": { cls: "qs-status--enroute",  label: "En route" },
  Reached:    { cls: "qs-status--reached",  label: "Reached"  },
  Done:       { cls: "qs-status--done",     label: "Done"     },
  Pending:    { cls: "qs-status--pending",  label: "Pending"  },
};

const ADVOCATE_STATUS_META = {
  NEW:                           { cls: "qs-status--new",        label: "New"                    },
  DOCUMENTS_PENDING:             { cls: "qs-status--docpending",  label: "Documents pending"      },
  UNDER_REVIEW:                  { cls: "qs-status--review",      label: "Under review"           },
  POA_PENDING:                   { cls: "qs-status--poapending",  label: "POA pending"            },
  ADVOCATE_ASSIGNED:             { cls: "qs-status--assigned",    label: "Advocate assigned"      },
  PETITION_PENDING:              { cls: "qs-status--inprogress",  label: "Petition pending"       },
  PETITION_FILED:                { cls: "qs-status--casefiled",   label: "Petition filed"         },
  DRIVER_BAIL_IN_PROGRESS:       { cls: "qs-status--hearing",     label: "Driver bail in progress"},
  DRIVER_BAIL_COMPLETED:         { cls: "qs-status--firverified", label: "Driver bail done"       },
  VEHICLE_BAIL_IN_PROGRESS:      { cls: "qs-status--settlement",  label: "Vehicle bail in progress"},
  VEHICLE_RELEASE_ORDER_PENDING: { cls: "qs-status--settlement",  label: "Release order pending"  },
  VEHICLE_RELEASED:              { cls: "qs-status--reached",     label: "Vehicle released"       },
  VEHICLE_DELIVERED:             { cls: "qs-status--done",        label: "Vehicle delivered"      },
  CASE_CLOSED:                   { cls: "qs-status--done",        label: "Case closed"            },
  CANCELLED:                     { cls: "qs-status--cancelled",   label: "Cancelled"              },
};

const SVC_ICON_CLASS = {
  crane:      "qs-svc-icon--crane",
  recovery:   "qs-svc-icon--recovery",
  advocate:   "qs-svc-icon--advocate",
  mechanic:   "qs-svc-icon--mechanic",
};

const SOURCE_LABEL = {
  admin:    "Admin",
  whatsapp: "WhatsApp bot",
};

const ADVOCATE_COMPLETED_STATUSES = new Set([
  "CASE_CLOSED", "CANCELLED", "VEHICLE_DELIVERED",
]);

// ─── Main component ─────────────────

export default function ServiceRequestCard({
  request,
  serviceType,
  expanded,
  onAssignVendor,
  onUpdateStatus,
  onTrack,
  isAssigning,
  onNavigateVendors,
}) {
  const navigate = useNavigate();
  const [assigningVendorId, setAssigningVendorId] = useState(null);

  const {
    id, customerName, phone, vehicleNo, serviceLabel,
    status, timeAgo, pickupLocation, dropLocation,
    documents = [], vendors = [],
    assignedVendor, assignedVendorId, urgent,
    accidentCaseNo, source,
  } = request;

  const handleOpen = () => {
      if (serviceType === "advocate") {
        navigate(`/admin/quick-services/legal-cases/${id}`, {
          state: { activeTab: "advocate" },
        });
        return;
      }
      navigate(`/admin/quick-services/${id}`);  
  };

  // Jump to the linked accident case without opening the QS detail page
  const handleOpenLinkedCase = (e) => {
    e.stopPropagation();
    navigate(`/admin/cases/${accidentCaseNo}`, {
      state: { activeTab: "On-Spot Services" },
    });
  };

  const isAdvocate  = serviceType === "advocate";
  const isCompleted = isAdvocate
    ? ADVOCATE_COMPLETED_STATUSES.has(status)
    : ["Done", "Cancelled"].includes(status);

  const canTrack  = TRACKABLE_SERVICES.has(serviceType);
  const canAssign = !assignedVendor && !isCompleted;

  const statusMeta = isAdvocate
    ? (ADVOCATE_STATUS_META[status] ?? ADVOCATE_STATUS_META.NEW)
    : (GENERIC_STATUS_META[status]  ?? GENERIC_STATUS_META.Pending);

  const handleAssign = async (vendor) => {
    setAssigningVendorId(vendor.id);
    await onAssignVendor(id, vendor.id, serviceType);
    setAssigningVendorId(null);
  };

  return (
    <div
      className={[
        "qs-card",
        urgent     ? "qs-card--urgent"   : "",
        expanded   ? "qs-card--expanded" : "",
        isAdvocate ? "qs-card--advocate" : "",
      ].filter(Boolean).join(" ")}
    >
      {/* ── Always-visible top row ── */}
      <div
        className="qs-card-top"
        onClick={handleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => e.key === "Enter" && handleOpen()}
      >
        <div className={`qs-svc-icon ${SVC_ICON_CLASS[serviceType] || ""}`}>
          <ServiceIcon type={serviceType} />
        </div>

        <div className="qs-card-info">
          <div className="qs-card-id-row">
            <span className="qs-card-id">{id}</span>
            {urgent && (
              <span className="qs-urgent-tag">
                <AlertSmIcon /> Urgent
              </span>
            )}
            {/* Linked accident case (click to open the case) */}
            {accidentCaseNo && (
              <button
                type="button"
                className="qs-chip qs-chip--linked"
                title="Open linked accident case"
                onClick={handleOpenLinkedCase}
              >
                🔗 Case {accidentCaseNo}
              </button>
            )}
            {/* Where the request was created */}
            {source && (
              <span className={`qs-chip qs-chip--${source}`}>
                {SOURCE_LABEL[source] || source}
              </span>
            )}
          </div>
          <div className="qs-card-name">
            {customerName}
            <span className="qs-card-vehicle"> · {vehicleNo}</span>
          </div>
          <div className="qs-card-meta">
            <MetaChip icon={<PackageIcon />}   text={serviceLabel} />
            <MetaChip icon={<ClockIcon />}     text={timeAgo} />
            <MetaChip icon={<PhoneIcon />}     text={phone} />
            {documents.length > 0 && (
              <MetaChip
                icon={<PaperclipIcon />}
                text={`${documents.length} doc${documents.length > 1 ? "s" : ""}`}
              />
            )}
          </div>
        </div>

        <div className="qs-card-right">
          <span className={`qs-status-pill ${statusMeta.cls}`}>
            {statusMeta.label}
          </span>
          <span className="qs-card-vendor-tag">
            {assignedVendor
              ? (isAdvocate ? `Adv: ${assignedVendor}` : assignedVendor)
              : "Unassigned"}
          </span>
        </div>
      </div>

      {/* ── Bottom action bar ── */}
      <div className="qs-card-bottom">
        <div className="qs-card-loc">
          <PinIcon />
          <span>
            {pickupLocation}
            {dropLocation ? ` → ${dropLocation}` : ""}
          </span>
        </div>
        <div className="qs-card-actions">
          {canAssign && (
            <button
              className="wd-btn wd-btn-primary qs-btn-sm"
              onClick={(e) => { e.stopPropagation(); handleOpen(); }}
            >
              <UserPlusIcon />
              {isAdvocate ? "Manage case" : "Assign"}
            </button>
          )}
          {assignedVendor && canTrack && (
            <button
              className="wd-btn wd-btn-secondary qs-btn-sm"
              onClick={(e) => { e.stopPropagation(); onTrack(request); }}
            >
              <MapPinIcon /> Track
            </button>
          )}
          <button
            className="wd-btn wd-btn-secondary qs-btn-sm qs-btn-icon"
            onClick={(e) => { e.stopPropagation(); handleOpen(); }}
            aria-label={expanded ? "Collapse" : "Expand"}
          >
            <ChevronRightIcon />
          </button>
        </div>
      </div>

      {/* ── Expanded panel ── */}
     
    </div>
  );
}

function MetaChip({ icon, text }) {
  return (
    <span className="qs-meta-chip">{icon}{text}</span>
  );
}

function ServiceIcon({ type }) {
  switch (type) {
    case "crane":      return <CraneIcon />;
    case "recovery":   return <TruckIcon />;
    case "advocate":   return <GavelIcon />;
    case "mechanic":   return <ToolsIcon />;
    case "inspection": return <ClipboardIcon />;
    default:           return <BoltIcon />;
  }
}